import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, ease as mmEase } from "@/lib/motion";
import { Eye, EyeOff, ArrowRight, Lock, Mail, Sparkles, ShieldCheck, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import AuthThreadMini from "@/components/auth/AuthThreadMini";
import { useAuth } from "@/context/AuthContext";

const ez = mmEase.expo;
const fv = (delay) => ({
  initial: { opacity: 0, x: 16, filter: "blur(3px)" },
  animate: { opacity: 1, x: 0,  filter: "blur(0px)" },
  transition: { duration: 0.48, ease: ez, delay },
});

const TOTP_REQUIRED = "Two-factor authentication code required";

export default function Login() {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [show, setShow]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [step, setStep]         = useState("credentials"); // "credentials" | "2fa"
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from?.pathname || "/app/dashboard";

  useEffect(() => {
    if (location.state?.reason === "idle") {
      toast.info("You were signed out for your security after a period of inactivity.");
    }
  }, [location.state]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const code = step === "2fa" ? totpCode.replace(/\s/g, "") : null;
      const user = await login(email.trim(), password, code);
      navigate(user.role === "admin" ? "/admin" : from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === TOTP_REQUIRED) {
        setStep("2fa");
        setError(null);
      } else {
        setError(detail || "Invalid credentials. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  function fillDemo() {
    setEmail("demo@moneymeet.com");
    setPassword("Demo@12345");
    toast.info("Demo credentials filled — click Continue to sign in.");
  }

  return (
    <div className="min-h-screen flex">
      {/* ── Left — brand panel ───────────────────────────────── */}
      <div className="hidden lg:flex w-[460px] xl:w-[520px] flex-col hero-gradient grain relative overflow-hidden">
        <div
          className="absolute top-[18%] left-[8%] w-72 h-72 rounded-full blur-3xl animate-orb pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.20) 0%, transparent 70%)" }}
        />
        <div
          className="absolute bottom-[15%] right-[5%] w-56 h-56 rounded-full blur-3xl animate-orb-b pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(6,182,212,0.14) 0%, transparent 70%)" }}
        />

        <div className="relative z-10 p-10 flex flex-col h-full">
          <Link to="/"><Logo variant="dark" size="md" /></Link>

          <div className="flex-1 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: ez }}
            >
              <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-4">
                Welcome back
              </p>
              <h1
                className="font-display font-black text-white leading-[1.08] tracking-tight mb-5 text-balance"
                style={{ fontSize: "clamp(2.2rem, 3.5vw, 3.2rem)", letterSpacing: "-0.04em" }}
              >
                Your money is<br />waiting for you.
              </h1>
              <p className="text-white/50 text-[14px] leading-relaxed max-w-sm">
                Access your wallets, send money to 45+ countries, and track every transfer in real time.
              </p>
            </motion.div>

            {/* The Thread — ambient Transfer indicator */}
            <motion.div
              className="mt-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.45, ease: ez }}
            >
              <AuthThreadMini />
            </motion.div>
          </div>

          <p className="text-white/22 text-[11px]">
            © {new Date().getFullYear()} Mikkshebs System Technology S.R.L.
            {process.env.NODE_ENV !== "production" && " (simulation)"}
          </p>
        </div>
      </div>

      {/* ── Right — form panel ───────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center bg-white px-5 py-12">
        <div className="w-full max-w-[380px]">
          <div className="lg:hidden mb-8">
            <Link to="/"><Logo variant="light" size="md" /></Link>
          </div>

          <motion.div {...fv(0)}>
            <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-2">
              MoneyMeet Pay
            </p>
            <h2
              className="font-display font-black text-navy tracking-tight mb-1"
              style={{ fontSize: "1.7rem", letterSpacing: "-0.035em" }}
            >
              Sign in
            </h2>
            <p className="text-sm text-muted-foreground mb-8">
              Enter your credentials to continue
            </p>
          </motion.div>

          {step === "credentials" ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <motion.div {...fv(0.06)}>
                  <label className="input-label" htmlFor="lg-email">Email address</label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      id="lg-email"
                      data-testid="login-email-input"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="input-field pl-9"
                    />
                  </div>
                </motion.div>

                <motion.div {...fv(0.11)}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="input-label" htmlFor="lg-password" style={{ marginBottom: 0 }}>
                      Password
                    </label>
                    <Link
                      to="/forgot"
                      data-testid="login-forgot-password-link"
                      className="text-[11px] font-semibold text-emerald hover:text-emerald-hover transition-colors"
                    >
                      Forgot?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      id="lg-password"
                      data-testid="login-password-input"
                      type={show ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="input-field pl-9 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShow((p) => !p)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy transition-colors"
                      aria-label={show ? "Hide password" : "Show password"}
                    >
                      {show ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </motion.div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
                    role="alert"
                  >
                    {error}
                  </motion.div>
                )}

                <motion.div {...fv(0.16)}>
                  <button
                    type="submit"
                    data-testid="login-submit-button"
                    disabled={loading}
                    className="btn-primary w-full justify-center py-3.5 text-sm mt-1 disabled:opacity-55 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                        Signing in…
                      </span>
                    ) : (
                      <>Continue <ArrowRight size={14} /></>
                    )}
                  </button>
                </motion.div>
              </form>

              {process.env.NODE_ENV !== "production" && (
                <motion.div {...fv(0.22)} className="mt-3">
                  <button
                    type="button"
                    onClick={fillDemo}
                    className="w-full py-3 text-[13px] font-medium text-muted-foreground border border-border/70 rounded-full hover:border-border hover:text-navy hover:bg-secondary/40 transition-all flex items-center justify-center gap-2"
                  >
                    <Sparkles size={13} className="text-amber-500" />
                    Use demo credentials
                  </button>
                </motion.div>
              )}
            </>
          ) : (
            <motion.div
              key="2fa"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, ease: ez }}
            >
              <button
                type="button"
                onClick={() => { setStep("credentials"); setTotpCode(""); setError(null); }}
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-muted-foreground hover:text-navy transition-colors mb-6"
              >
                <ChevronLeft size={14} /> Back
              </button>
              <div className="flex items-center gap-3 mb-6 p-4 bg-emerald/[0.06] border border-emerald/20 rounded-2xl">
                <ShieldCheck size={22} className="text-emerald shrink-0" strokeWidth={1.8} />
                <div>
                  <p className="text-[13px] font-semibold text-navy">Two-factor authentication</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Enter the 6-digit code from your authenticator app.</p>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="input-label" htmlFor="lg-totp">Authenticator code</label>
                  <input
                    id="lg-totp"
                    data-testid="login-totp-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9 ]*"
                    maxLength={7}
                    autoComplete="one-time-code"
                    required
                    autoFocus
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="123456"
                    className="input-field text-center tracking-[0.3em] text-lg font-bold"
                  />
                </div>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
                    role="alert"
                  >
                    {error}
                  </motion.div>
                )}
                <button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loading || totpCode.length < 6}
                  className="btn-primary w-full justify-center py-3.5 text-sm disabled:opacity-55 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                      Verifying…
                    </span>
                  ) : (
                    <>Verify & sign in <ArrowRight size={14} /></>
                  )}
                </button>
              </form>
              <p className="text-center text-[11px] text-muted-foreground mt-4">
                Lost access to your authenticator?{" "}
                <Link to="/2fa" state={{ email, password }} className="text-emerald font-semibold hover:text-emerald-hover">
                  Use backup code
                </Link>
              </p>
            </motion.div>
          )}

          <motion.p
            {...fv(0.28)}
            className="text-center text-[13px] text-muted-foreground mt-8"
          >
            New to MoneyMeet?{" "}
            <Link
              to="/register"
              data-testid="login-register-link"
              className="font-bold text-emerald hover:text-emerald-hover transition-colors"
            >
              Create free account →
            </Link>
          </motion.p>
        </div>
      </div>
    </div>
  );
}
